import type { FormEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
} from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import type { CycleProcedure, CycleProcedureStatus, TreatmentStage } from '../../../types/admin';
import {
  useCreateCycleProcedure,
  useUpdateCycleProcedure,
  useDeleteCycleProcedure,
  useReorderCycleProcedures,
} from '../hooks/useProcedures';
import styles from './StepPanel.module.scss';

type PreparingPanelProps = {
  readonly patientId: string;
  readonly treatmentId: string;
  readonly stage: TreatmentStage;
};

const STATUS_CLASS: Record<CycleProcedureStatus, string> = {
  pending: styles.statusPending,
  completed: styles.statusCompleted,
  verified: styles.statusVerified,
};

const STATUS_LABEL_KEY: Record<CycleProcedureStatus, string> = {
  pending: 'preparing.statusPending',
  completed: 'preparing.statusCompleted',
  verified: 'preparing.statusVerified',
};

const PROCEDURE_OPTIONS: readonly string[] = [
  'Професійна гігієна',
  'Терапевтична консультація',
  'Терапевтична підготовка (лікування - карієс або заміна коронок)',
  'Зняття ретейнера + сканування в один візит',
  'Заміна мостів на одиночні конструкції',
  'Панорамний знімок або КТ',
  'Видалення 8-их зубів',
  'Консультація гнатолога',
  'Консультація пародонтолога',
  'Встановлення атачментів',
  'Застосування накладок на капи',
  'Гнатологічне лікування',
  'Пародонтологічне лікування',
  '3Д сканування'
];

function sortBySortOrder(procedures: readonly CycleProcedure[]): readonly CycleProcedure[] {
  return [...procedures].sort((a, b) => a.sort_order - b.sort_order);
}

export function PreparingPanel({ patientId, treatmentId, stage }: PreparingPanelProps) {
  const { t } = useTranslation();
  const sortedProcedures = useMemo(() => sortBySortOrder(stage.procedures ?? []), [stage.procedures]);
  const createMutation = useCreateCycleProcedure(patientId, treatmentId, stage.id);
  const updateMutation = useUpdateCycleProcedure(patientId, treatmentId, stage.id);
  const deleteMutation = useDeleteCycleProcedure(patientId, treatmentId, stage.id);
  const reorderMutation = useReorderCycleProcedures(patientId, treatmentId, stage.id);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isRequired, setIsRequired] = useState(true);

  const [orderedIds, setOrderedIds] = useState<readonly string[]>(() =>
    sortedProcedures.map((proc) => proc.id),
  );

  useEffect(() => {
    setOrderedIds(sortedProcedures.map((proc) => proc.id));
  }, [sortedProcedures]);

  const displayedProcedures = useMemo<readonly CycleProcedure[]>(() => {
    const byId = new Map(sortedProcedures.map((proc) => [proc.id, proc]));
    const ordered: CycleProcedure[] = [];
    for (const id of orderedIds) {
      const proc = byId.get(id);
      if (proc) ordered.push(proc);
    }
    // Append any procedures not yet in our local order (e.g. just created).
    for (const proc of sortedProcedures) {
      if (!orderedIds.includes(proc.id)) ordered.push(proc);
    }
    return ordered;
  }, [sortedProcedures, orderedIds]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = orderedIds.indexOf(String(active.id));
    const newIndex = orderedIds.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const nextOrder = arrayMove([...orderedIds], oldIndex, newIndex);
    setOrderedIds(nextOrder);
    reorderMutation.mutate(nextOrder);
  }

  async function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmedTitle = title.trim();
    if (!trimmedTitle) return;
    try {
      await createMutation.mutateAsync({
        title: trimmedTitle,
        description: description.trim() || undefined,
        is_required: isRequired,
        sort_order: displayedProcedures.length,
      });
      setTitle('');
      setDescription('');
      setIsRequired(true);
    } catch {
      return;
    }
  }

  function handleVerify(proc: CycleProcedure) {
    updateMutation.mutate({
      procedureId: proc.id,
      payload: { status: 'verified' },
    });
  }

  function handleMarkComplete(proc: CycleProcedure) {
    updateMutation.mutate({
      procedureId: proc.id,
      payload: { status: 'completed' },
    });
  }

  function handleDelete(procedureId: string) {
    deleteMutation.mutate(procedureId);
  }

  async function handleSaveEdit(
    procedureId: string,
    payload: { title: string; description: string | null },
  ) {
    await updateMutation.mutateAsync({ procedureId, payload });
  }

  const reorderInFlight = reorderMutation.isPending;
  const doneCount = displayedProcedures.filter((proc) => proc.status !== 'pending').length;
  const allDone = displayedProcedures.length > 0 && doneCount === displayedProcedures.length;

  return (
    <div className={styles.panel}>
      <header className={styles.sectionHeader}>
        <div className={styles.sectionHeaderText}>
          <h4 className={styles.title}>{t('preparing.title')}</h4>
          <p className={styles.subtitle}>{t('preparing.subtitle')}</p>
        </div>
        {displayedProcedures.length > 0 && (
          <span
            className={`${styles.sectionCountChip} ${allDone ? styles.sectionCountChipDone : ''}`}
          >
            {t('preparing.completedSummary', {
              done: doneCount,
              total: displayedProcedures.length,
            })}
          </span>
        )}
      </header>

      {displayedProcedures.length > 0 ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={displayedProcedures.map((proc) => proc.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className={styles.list}>
              {displayedProcedures.map((proc, index) => (
                <SortableProcedureRow
                  key={proc.id}
                  proc={proc}
                  position={index + 1}
                  statusLabel={t(STATUS_LABEL_KEY[proc.status])}
                  reorderDisabled={reorderInFlight}
                  updateInFlight={updateMutation.isPending}
                  deleteInFlight={deleteMutation.isPending}
                  onMarkComplete={() => handleMarkComplete(proc)}
                  onVerify={() => handleVerify(proc)}
                  onDelete={() => handleDelete(proc.id)}
                  onSaveEdit={(payload) => handleSaveEdit(proc.id, payload)}
                  optionalLabel={t('preparing.optional')}
                  markCompleteLabel={t('preparing.markComplete')}
                  verifyLabel={t('preparing.verify')}
                  removeLabel={t('preparing.remove')}
                  editLabel={t('preparing.edit', { defaultValue: 'Редагувати' })}
                  saveLabel={t('preparing.save', { defaultValue: 'Зберегти' })}
                  cancelLabel={t('preparing.cancel', { defaultValue: 'Скасувати' })}
                  savingLabel={t('preparing.saving', { defaultValue: 'Збереження...' })}
                  editTitleLabel={t('preparing.procedureTitleLabel')}
                  editDescriptionLabel={t('preparing.descriptionLabel')}
                  titlePlaceholder={t('preparing.procedureTitlePlaceholder')}
                  descriptionPlaceholder={t('preparing.descriptionPlaceholder')}
                  dragHandleLabel={t('preparing.dragHandle', { defaultValue: 'Перетягнути' })}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className={styles.emptyState}>
          <p>{t('preparing.noProcedures')}</p>
          <p className={styles.mutedSmall}>{t('preparing.noProceduresHint')}</p>
        </div>
      )}

      {reorderMutation.error instanceof Error && (
        <p className={styles.inlineError}>{reorderMutation.error.message}</p>
      )}

      <hr className={styles.divider} />

      <form onSubmit={handleCreate} className={styles.formCard}>
        <h5 className={styles.formCardTitle}>{t('preparing.addSectionTitle')}</h5>

        <div className={styles.formGrid}>
          <label className={`${styles.field} ${styles.fieldFull}`}>
            <span>{t('preparing.procedureTitleLabel')}</span>
            <select value={title} onChange={(e) => setTitle(e.target.value)}>
              <option value="">{t('preparing.procedureTitlePlaceholder')}</option>
              {PROCEDURE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <label className={`${styles.field} ${styles.fieldFull}`}>
            <span>{t('preparing.descriptionLabel')}</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('preparing.descriptionPlaceholder')}
            />
          </label>
          <label className={styles.field}>
            <span>{t('preparing.required')}</span>
            <select
              value={isRequired ? 'yes' : 'no'}
              onChange={(e) => setIsRequired(e.target.value === 'yes')}
            >
              <option value="yes">{t('preparing.yes')}</option>
              <option value="no">{t('preparing.no')}</option>
            </select>
          </label>
        </div>

        <div className={styles.formFooterBar}>
          {createMutation.error instanceof Error && (
            <p className={styles.inlineError}>{createMutation.error.message}</p>
          )}
          <button
            type="submit"
            className={styles.primaryBtn}
            disabled={createMutation.isPending || !title.trim()}
          >
            {createMutation.isPending ? t('preparing.adding') : t('preparing.addProcedure')}
          </button>
        </div>
      </form>
    </div>
  );
}

type SortableProcedureRowProps = {
  readonly proc: CycleProcedure;
  readonly position: number;
  readonly statusLabel: string;
  readonly reorderDisabled: boolean;
  readonly updateInFlight: boolean;
  readonly deleteInFlight: boolean;
  readonly onMarkComplete: () => void;
  readonly onVerify: () => void;
  readonly onDelete: () => void;
  readonly onSaveEdit: (payload: { title: string; description: string | null }) => Promise<void>;
  readonly optionalLabel: string;
  readonly markCompleteLabel: string;
  readonly verifyLabel: string;
  readonly removeLabel: string;
  readonly editLabel: string;
  readonly saveLabel: string;
  readonly cancelLabel: string;
  readonly savingLabel: string;
  readonly editTitleLabel: string;
  readonly editDescriptionLabel: string;
  readonly titlePlaceholder: string;
  readonly descriptionPlaceholder: string;
  readonly dragHandleLabel: string;
};

function SortableProcedureRow({
  proc,
  position,
  statusLabel,
  reorderDisabled,
  updateInFlight,
  deleteInFlight,
  onMarkComplete,
  onVerify,
  onDelete,
  onSaveEdit,
  optionalLabel,
  markCompleteLabel,
  verifyLabel,
  removeLabel,
  editLabel,
  saveLabel,
  cancelLabel,
  savingLabel,
  editTitleLabel,
  editDescriptionLabel,
  titlePlaceholder,
  descriptionPlaceholder,
  dragHandleLabel,
}: SortableProcedureRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: proc.id,
    disabled: false,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(proc.title);
  const [editDescription, setEditDescription] = useState(proc.description ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  useEffect(() => {
    if (!isEditing) {
      setEditTitle(proc.title);
      setEditDescription(proc.description ?? '');
    }
  }, [proc.title, proc.description, isEditing]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 1 : undefined,
  };

  function startEdit() {
    setEditTitle(proc.title);
    setEditDescription(proc.description ?? '');
    setEditError(null);
    setIsEditing(true);
  }

  function cancelEdit() {
    setIsEditing(false);
    setEditError(null);
  }

  async function submitEdit() {
    const trimmedTitle = editTitle.trim();
    if (!trimmedTitle) return;
    const trimmedDescription = editDescription.trim();
    const original = proc.description ?? '';
    if (trimmedTitle === proc.title && trimmedDescription === original) {
      setIsEditing(false);
      return;
    }
    setIsSaving(true);
    setEditError(null);
    try {
      await onSaveEdit({
        title: trimmedTitle,
        description: trimmedDescription === '' ? null : trimmedDescription,
      });
      setIsEditing(false);
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  }

  const isDone = proc.status !== 'pending';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${styles.procedureRow} ${isDone ? styles.procedureRowDone : ''}`}
      data-dragging={isDragging}
      data-editing={isEditing ? 'true' : undefined}
    >
      <button
        type="button"
        className={styles.dragHandle}
        aria-label={dragHandleLabel}
        title={dragHandleLabel}
        disabled={reorderDisabled || isEditing}
        {...attributes}
        {...listeners}
      >
        <span aria-hidden="true">⋮⋮</span>
      </button>
      <span className={styles.procedureIndex} aria-hidden="true">
        {position}
      </span>
      <div className={styles.listItemInfo}>
        {isEditing ? (
          <div className={styles.procedureEditForm}>
            <label className={styles.field}>
              <span>{editTitleLabel}</span>
              <select
                value={PROCEDURE_OPTIONS.includes(editTitle) ? editTitle : ''}
                onChange={(e) => setEditTitle(e.target.value)}
                autoFocus
              >
                <option value="">{titlePlaceholder}</option>
                {PROCEDURE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
                {editTitle && !PROCEDURE_OPTIONS.includes(editTitle) && (
                  <option value={editTitle}>{editTitle}</option>
                )}
              </select>
            </label>
            <label className={styles.field}>
              <span>{editDescriptionLabel}</span>
              <textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder={descriptionPlaceholder}
              />
            </label>
            {editError && <p className={styles.inlineError}>{editError}</p>}
          </div>
        ) : (
          <>
            <span className={styles.procedureTitle}>
              {proc.title}
              {!proc.is_required && (
                <span className={styles.procedureOptionalTag}>{optionalLabel}</span>
              )}
            </span>
            {proc.description && (
              <span className={styles.procedureDescription}>{proc.description}</span>
            )}
          </>
        )}
      </div>
      <div className={styles.listItemActions}>
        {isEditing ? (
          <>
            <button
              type="button"
              className={styles.primaryBtn}
              disabled={isSaving || !editTitle.trim()}
              onClick={submitEdit}
            >
              {isSaving ? savingLabel : saveLabel}
            </button>
            <button
              type="button"
              className={styles.secondaryBtn}
              disabled={isSaving}
              onClick={cancelEdit}
            >
              {cancelLabel}
            </button>
          </>
        ) : (
          <>
            <span className={`${styles.statusBadge} ${STATUS_CLASS[proc.status]}`}>
              {statusLabel}
            </span>
            {proc.status === 'pending' && (
              <button
                type="button"
                className={`${styles.rowBtn} ${styles.rowBtnAccent}`}
                disabled={updateInFlight}
                onClick={onMarkComplete}
              >
                {markCompleteLabel}
              </button>
            )}
            {proc.status === 'completed' && (
              <button
                type="button"
                className={`${styles.rowBtn} ${styles.rowBtnAccent}`}
                disabled={updateInFlight}
                onClick={onVerify}
              >
                {verifyLabel}
              </button>
            )}
            <button
              type="button"
              className={styles.rowBtn}
              disabled={updateInFlight}
              onClick={startEdit}
            >
              {editLabel}
            </button>
            <button
              type="button"
              className={`${styles.rowBtn} ${styles.rowBtnDanger}`}
              disabled={deleteInFlight}
              onClick={onDelete}
              aria-label={removeLabel}
              title={removeLabel}
            >
              ✕
            </button>
          </>
        )}
      </div>
    </div>
  );
}
